import { useMemo, useState } from 'react';
import { db } from '../../lib/firebase';
import { removeDoc, saveDoc } from '../../lib/firestore-write';
import { useOrderedCol } from '../../lib/hooks';
import type { CourseDoc } from '../../lib/data-model';
import { ConfirmDialog, EmptyState, Loading, Modal, toast } from '../ui';
import { AdminToolbar, DeleteBtn, EditBtn, HiddenBtn, NewBtn, SwitchRow, SwapBar, errOf, useSaver } from './ListAdmin';

const HEX = /^#[0-9a-fA-F]{6}$/;

export function CoursesTab() {
  const courses = useOrderedCol<CourseDoc>('courses');
  const [editor, setEditor] = useState<{ id?: string } | null>(null);
  const [deleting, setDeleting] = useState<(CourseDoc & { id: string }) | null>(null);
  const [query, setQuery] = useState('');

  if (courses.loading) return <Loading label="Loading courses…" />;

  const toggle = (c: CourseDoc & { id: string }) =>
    saveDoc(db, 'courses', c.id, { ...c, hidden: !c.hidden })
      .then(() => toast('Saved', 'ok'))
      .catch(e => toast(errOf(e), 'err'));

  const del = async () => {
    if (!deleting) return;
    try {
      await removeDoc(db, 'courses', deleting.id);
      toast('Course deleted', 'ok');
    } catch (e) {
      toast(errOf(e), 'err');
    }
  };

  const shown = courses.data
    .filter(c => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return `${c.name} ${c.code} ${c.tagline}`.toLowerCase().includes(q);
    })
    .sort((a, b) => a.order - b.order);

  return (
    <div className="panel">
      <div className="admin-wrap">
        <AdminToolbar label={`${courses.data.length} COURSE${courses.data.length === 1 ? '' : 'S'}`} query={query} onQuery={setQuery}>
          <NewBtn onClick={() => setEditor({})}>+ New course</NewBtn>
        </AdminToolbar>

        {courses.data.length === 0 ? (
          <EmptyState glyph="▤" title="No courses yet" body="Add your first course to start building the catalogue." />
        ) : shown.length === 0 ? (
          <EmptyState glyph="⌕" title="No matches" body={`Nothing matches “${query}”. Try a different name or code.`} />
        ) : (
          <div className="admin-grid">
            {shown.map(c => (
              <div key={c.id} className={`admin-card${c.hidden ? ' off' : ''}`}>
                <div className="card-top">
                  <span
                    className="glyph-tile"
                    style={{ background: c.tint || 'var(--t-green)', color: c.accent }}
                    aria-hidden
                  >
                    {c.glyph || 'A'}
                  </span>
                  <span className="grow" />
                  <span className="pill line">{c.code}</span>
                  <span className={`dot ${c.hidden ? 'off' : 'on'}`} title={c.hidden ? 'Hidden' : 'Published'} />
                </div>
                <h3>{c.name}</h3>
                <p>{c.tagline || 'No tagline yet.'}</p>
                <div className="card-foot">
                  <SwapBar coll="courses" list={courses.data} id={c.id} hidden={c.hidden} label={c.name} />
                  <HiddenBtn hidden={!!c.hidden} onClick={() => void toggle(c)} />
                  <EditBtn onClick={() => setEditor({ id: c.id })} />
                  <DeleteBtn onClick={() => setDeleting(c)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editor && (
        <CourseForm
          order={courses.data.length ? Math.max(...courses.data.map(c => c.order)) + 1 : 0}
          existing={editor.id ? courses.data.find(c => c.id === editor.id) : undefined}
          onClose={() => setEditor(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete course?"
          confirmLabel="Delete course"
          body={
            <p>
              Delete <strong>{deleting.name}</strong>? This only removes the course shell — if it has chapters,
              delete those first from the other tabs. This cannot be undone.
            </p>
          }
          onConfirm={del}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function CourseForm({ order, existing, onClose }: { order: number; existing?: CourseDoc & { id: string }; onClose: () => void }) {
  const id = useMemo(() => existing?.id ?? `c${Date.now().toString(36)}`, [existing]);
  const [code, setCode] = useState(existing?.code ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [tagline, setTagline] = useState(existing?.tagline ?? '');
  const [glyph, setGlyph] = useState(existing?.glyph ?? 'A');
  const [accent, setAccent] = useState(existing?.accent ?? '#2FBF6D');
  const [tint, setTint] = useState(existing?.tint ?? '');
  const [deep, setDeep] = useState(existing?.deep ?? '');
  const [hidden, setHidden] = useState(existing?.hidden ?? false);

  const { errors, saving, save } = useSaver(
    'courses',
    id,
    () => {
      const d: Record<string, unknown> = { order, code, name, tagline, glyph, accent, hidden };
      if (tint.trim() && HEX.test(tint.trim())) d.tint = tint.trim();
      if (deep.trim() && HEX.test(deep.trim())) d.deep = deep.trim();
      return d;
    },
    onClose
  );

  return (
    <Modal
      title={existing ? 'Edit course' : 'New course'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save course'}</button>
        </>
      }
    >
      <div className="row2">
        <div className="field">
          <label>Code</label>
          <input type="text" value={code} onChange={e => setCode(e.target.value)} placeholder="MATH 152" />
          {errors.code ? <div className="error">{errors.code}</div> : null}
        </div>
        <div className="field">
          <label>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Calculus II" />
          {errors.name ? <div className="error">{errors.name}</div> : null}
        </div>
      </div>

      <div className="field">
        <label>Tagline</label>
        <input type="text" value={tagline} onChange={e => setTagline(e.target.value)} />
        {errors.tagline ? <div className="error">{errors.tagline}</div> : null}
      </div>

      <div className="row2">
        <div className="field">
          <label>Glyph</label>
          <input type="text" maxLength={2} value={glyph} onChange={e => setGlyph(e.target.value)} />
          {errors.glyph ? <div className="error">{errors.glyph}</div> : null}
        </div>
        <div className="field">
          <label>Accent</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              value={HEX.test(accent) ? accent : '#2FBF6D'}
              onChange={e => setAccent(e.target.value)}
              style={{ width: 44, height: 38, padding: 0, border: '1px solid var(--line)', borderRadius: 8 }}
              aria-label="Accent colour"
            />
            <input type="text" value={accent} onChange={e => setAccent(e.target.value)} />
          </div>
          {errors.accent ? <div className="error">{errors.accent}</div> : null}
        </div>
      </div>

      <div className="row2">
        <div className="field">
          <label>Tint (optional)</label>
          <input type="text" value={tint} onChange={e => setTint(e.target.value)} placeholder="#E4F6EC" />
        </div>
        <div className="field">
          <label>Deep (optional)</label>
          <input type="text" value={deep} onChange={e => setDeep(e.target.value)} placeholder="#1F6E45" />
        </div>
      </div>

      <SwitchRow label="Hidden" on={hidden} onToggle={() => setHidden(v => !v)} hint="Hidden courses stay private to you." />
    </Modal>
  );
}
